import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as FileSaver from 'file-saver';
import * as Papa from 'papaparse';
import { map, switchMap, withLatestFrom, catchError, mergeMap } from 'rxjs/operators';
import { feedbackWithMember } from '../feedback/feedback.selectors';
import { CHECK_INS, getAll, getEntities } from '../reducers';
import * as selectors from '../reducers/index';
import { getActiveCustomers, getActiveMembers } from '../statistics/statistics.selectors';
import { DataExportActions } from './data-export.actions';
import { from, of } from 'rxjs';
import {
  CategoriesService,
  CommentsService,
  CustomerActivityDto,
  CustomersService,
  FeedbackService,
  MemberActivityDto,
  MemberDto,
  MembersService,
  CheckInDto,
  OrganisationActivityDto,
  OrganisationDto,
  QuestionDto,
  QuestionsService,
  StatisticsService,
} from '@careapp-group/ng-sdk';
import { POLL, RATING, YESNO } from '@careapp-group/core';
import { formatISO9075, parseISO } from 'date-fns';
import { statistics } from '..';
import { environment as CaEnvironment } from '../../../environments/environment';

@Injectable()
export class DataExportEffects {
  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private membersService: MembersService,
    private customersService: CustomersService,
    private feedbackService: FeedbackService,
    private categoryService: CategoriesService,
    private questionsService: QuestionsService,
    private statisticsService: StatisticsService,
    private statsApi: StatisticsService,
    private commentsService: CommentsService
  ) {}
  exportCustomerStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportCustomersStatistics),
      withLatestFrom(this.store.select(getActiveCustomers)),
      map(([action, data]) => {
        return data.map((row) => ({
          firstName: row.customer.firstName,
          lastName: row.customer.lastName,
          'content created': row.memoCreated,
          'check ins': row.checkInCreated,
          views: row.memoViewed,
          hearts: row.memoHearted,
          comments: row.memoCommented,
          'family representatives': row.assignedRoles?.CUSTOMER_FAMILY_REPRESENTATIVE || 0,
          'family followers': row.assignedRoles?.CUSTOMER_FAMILY || 0,
        }));
      }),

      map((data) => Papa.unparse(data)),
      map((data) =>
        DataExportActions.requestSaveFile({
          filename: 'customer-statistics.csv',
          mimeType: 'text/csv',
          data,
        })
      )
    )
  );

  exportMembersStatistics = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportMembersStatistics),
      withLatestFrom(this.store.select(getActiveMembers)),
      map(([action, data]) =>
        data.map((row) => ({
          firstName: row.member.firstName,
          lastName: row.member.lastName,
          'content created': row.memoCreated,
          'check ins': row.checkInCreated,
          views: row.memoViewed,
          hearts: row.memoHearted,
          comments: row.memoCommented,
        }))
      ),
      map((data) => Papa.unparse(data)),
      map((data) =>
        DataExportActions.requestSaveFile({
          filename: 'staff-statistics.csv',
          mimeType: 'text/csv',
          data,
        })
      )
    )
  );

  exportCustomerStatisticsForOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportCustomersStatisticsForOrganisations),
      withLatestFrom(
        this.store.select(getEntities('organisations')),
        this.store.select(selectors.statistics.getFilters)
      ),
      switchMap(async ([action, organisations, filter]) => {
        // we need to get all the customers for each of the organisations
        let combinedCustomers = {};
        let orgStats: {
          [orgId: string]: CustomerActivityDto[];
        } = {};
        for (const organisationId of action.organisationIds) {
          const orgCustomers = await this.customersService
            .listCustomers({ organisationId })
            .toPromise();
          const entities = orgCustomers.reduce(
            (acc, entity) => ({ ...acc, [entity.id]: entity }),
            {}
          );
          combinedCustomers = { ...combinedCustomers, ...entities };
        }

        // now we need to get the customer stats for each organisation
        const startDate = filter.startDate?.toISOString();
        const endDate = filter.endDate?.toISOString();
        orgStats = await this.statisticsService
          .getOrganisationCustomerActivity({
            organisationActivityRequestDto: {
              organisationIds: action.organisationIds,
              startDate,
              endDate,
            },
          })
          .toPromise();

        // merge to csv
        let combinedStats = [];
        for (const orgId of action.organisationIds) {
          combinedStats = [
            ...combinedStats,
            ...orgStats[orgId].map((row) => ({
              organisation: organisations[orgId].name,
              firstName: combinedCustomers[row.customerId]?.firstName || '',
              lastName: combinedCustomers[row.customerId]?.lastName || '',
              externalId: combinedCustomers[row.customerId]?.externalId || '',
              'content created': row.memoCreated,
              'check ins': row.checkInCreated,
              views: row.memoViewed,
              hearts: row.memoHearted,
              comments: row.memoCommented,
              'family representatives': row.assignedRoles?.CUSTOMER_FAMILY_REPRESENTATIVE || 0,
              'family followers': row.assignedRoles?.CUSTOMER_FAMILY || 0,
            })),
          ];
        }
        const csv = Papa.unparse(combinedStats);

        return DataExportActions.requestSaveFile({
          filename: 'customer-statistics-combined.csv',
          mimeType: 'text/csv',
          data: csv,
        });
      }),
      catchError((error) => {
        return of(DataExportActions.responseFailDataExport({ error }));
      })
    )
  );
  exportMembersStatisticsForOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportMembersStatisticsForOrganisations),
      withLatestFrom(
        this.store.select(getEntities('organisations')),
        this.store.select(selectors.statistics.getFilters)
      ),
      switchMap(async ([action, organisations, filter]) => {
        // we need to get all the customers for each of the organisations
        let combinedMembers: { [memberId: string]: MemberDto } = {};
        let orgStats: {
          [key: string]: Array<MemberActivityDto>;
        } = {};
        for (const organisationId of action.organisationIds) {
          const orgCustomers = await this.membersService
            .listMembers({ organisationId })
            .toPromise();
          const entities = orgCustomers.reduce(
            (acc, entity) => ({ ...acc, [entity.id]: entity }),
            {}
          );
          combinedMembers = { ...combinedMembers, ...entities };
        }

        // now we need to get the customer stats for each organisation
        const startDate = filter.startDate?.toISOString();
        const endDate = filter.endDate?.toISOString();
        orgStats = await this.statisticsService
          .getOrganisationMemberActivity({
            organisationActivityRequestDto: {
              organisationIds: action.organisationIds,
              startDate,
              endDate,
            },
          })
          .toPromise();

        // merge to csv
        let combinedStats = [];
        for (const orgId of action.organisationIds) {
          combinedStats = [
            ...combinedStats,
            ...orgStats[orgId].map((row) => ({
              organisation: organisations[orgId].name,
              firstName: combinedMembers[row.memberId].firstName,
              lastName: combinedMembers[row.memberId].lastName,
              externalId:
                (combinedMembers[row.memberId].permissions ?? []).find(
                  (permission) => permission.modelId === orgId
                )?.externalId ?? null,
              'content created': row.memoCreated,
              'check ins': row.checkInCreated,
              views: row.memoViewed,
              hearts: row.memoHearted,
              comments: row.memoCommented,
            })),
          ];
        }
        const csv = Papa.unparse(combinedStats);

        return DataExportActions.requestSaveFile({
          filename: 'staff-statistics-combined.csv',
          mimeType: 'text/csv',
          data: csv,
        });
      }),
      catchError((error) => {
        return of(DataExportActions.responseFailDataExport({ error }));
      })
    )
  );

  exportContentStatisticsForOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportContentStatisticsForOrganisations),
      withLatestFrom(
        this.store.select(getEntities('organisations')),
        this.store.select(selectors.statistics.getFilters)
      ),
      switchMap(async ([action, organisations, filter]) => {
        // we need to get all the customers for each of the organisations
        let combinedCategories = {};
        let combinedCategoryNames = new Set<string>();
        let orgStats: { [id: string]: OrganisationActivityDto };

        const categories = await this.categoryService.listCategories({}).toPromise();
        combinedCategoryNames = new Set([
          ...combinedCategoryNames.values(),
          ...categories.map((c) => c.name),
        ]);
        const entities = categories.reduce(
          (acc: any, entity: any) => ({ ...acc, [entity.id]: entity }),
          {}
        );
        combinedCategories = { ...combinedCategories, ...entities };

        // now we need to get the customer stats for each organisation
        orgStats = await this.statsApi
          .getOrganisationActivity({
            organisationActivityRequestDto: {
              organisationIds: action.organisationIds,
              startDate: filter.startDate?.toISOString(),
              endDate: filter.endDate?.toISOString(),
            },
          })
          .toPromise();

        // merge to csv
        let combinedStats = [];
        const usedCategories = new Set();
        for (const orgId of action.organisationIds) {
          const categoryStats = {};
          Object.keys(orgStats[orgId].memoCreatedByCategory).forEach((category) => {
            usedCategories.add(combinedCategories[category].name);
            categoryStats[combinedCategories[category].name] =
              orgStats[orgId].memoCreatedByCategory[category];
          });
          combinedStats = [
            ...combinedStats,
            {
              site: organisations[orgId].name,
              ...orgStats[orgId],
              'active customers': orgStats[orgId].customers,
              'active customers with family': orgStats[orgId].customersWithFamily,
              'active customers with content': orgStats[orgId].customersWithContent,
              'active customers with family and content':
                orgStats[orgId].customersWithFamilyWithContent,
              notes: orgStats[orgId].memoCreatedByType.note,
              photos: orgStats[orgId].memoCreatedByType.gallery,
              videos: orgStats[orgId].memoCreatedByType.video,
              'content created': orgStats[orgId].memoCreated,
              'content shared with family': orgStats[orgId].memoShared,
              'check ins': orgStats[orgId].checkInCreated,
              'video chats': orgStats[orgId].chatCreated,
              feedback: orgStats[orgId].feedbackCreated,
              notices: orgStats[orgId].announcementCreated,
              questions: orgStats[orgId].questionCreated,
              'content views': orgStats[orgId].memoViewed,
              'notice views': orgStats[orgId].announcementViewed,
              hearts: orgStats[orgId].memoHearted,
              comments: orgStats[orgId].memoCommented,
              'question answers': orgStats[orgId].questionAnswerCreated,
              ...categoryStats,
            },
          ];
        }
        const csv = Papa.unparse({
          fields: [
            'site',
            'active customers',
            'active customers with family',
            'active customers with content',
            'active customers with family and content',
            'notes',
            'photos',
            'videos',
            'content created',
            'content shared with family',
            'check ins',
            'video chats',
            'feedback',
            'notices',
            'questions',
            'content views',
            'notice views',
            'hearts',
            'comments',
            'question answers',
            ...Array.from(usedCategories).sort(),
          ],
          data: combinedStats,
        });

        return DataExportActions.requestSaveFile({
          filename: 'content-statistics-combined.csv',
          mimeType: 'text/csv',
          data: csv,
        });
      }),
      catchError((error) => {
        console.error(error);
        return of(DataExportActions.responseFailDataExport({ error }));
      })
    )
  );

  exportFeedback = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportFeedback),
      withLatestFrom(this.store.select(feedbackWithMember)),
      map(([action, data]) =>
        data.map((row) => ({
          firstName: row.member.firstName,
          lastName: row.member.lastName,
          email: row.member.email,
          date: row.created,
          rating: row.rating,
          message: row.message,
        }))
      ),
      map((data) => Papa.unparse(data)),
      map((data) =>
        DataExportActions.requestSaveFile({
          filename: 'feedback.csv',
          mimeType: 'text/csv',
          data,
        })
      )
    )
  );

  exportFeedbackForOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportFeedbackForOrganisations),
      withLatestFrom(
        this.store.select(getEntities('organisations')),
        this.store.select(selectors.statistics.getFilters)
      ),
      switchMap(([action, organisations, filters]) =>
        from(this.buildFeedbackCsvForOrganisations(action, organisations, filters)).pipe(
          map((csv: string) =>
            DataExportActions.requestSaveFile({
              filename: 'feedback-combined.csv',
              mimeType: 'text/csv',
              data: csv,
            })
          ),
          catchError((error) => {
            return of(DataExportActions.responseFailDataExport({ error }));
          })
        )
      )
    )
  );

  exportRecentCommentsForOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportRecentCommentsForOrganisations),
      withLatestFrom(
        this.store.select(getEntities('organisations')),
        this.store.select(selectors.statistics.getFilters)
      ),
      switchMap(([action, organisations, filters]) =>
        from(
          this.buildCommentsCsvForOrganisations(action, organisations, filters)
        ).pipe(
          map((csv: string) =>
            DataExportActions.requestSaveFile({
              filename: 'comments-combined.csv',
              mimeType: 'text/csv',
              data: csv,
            })
          ),
          catchError((error) => {
            return of(DataExportActions.responseFailDataExport({ error }));
          })
        )
      )
    )
  );

  exportCustomerCheckIns = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.exportCheckInsForCustomer),
      withLatestFrom(this.store.select(getAll(CHECK_INS))),
      map(([action, checkIns]) => {
        return (checkIns as Array<CheckInDto>).filter(
          (checkIn) => checkIn.customerId === action.customerId,
        );
      }),
      map((checkIns) =>
        checkIns.map((ci) => ({
          created: formatISO9075(parseISO(ci.created)),
          label: ci.label,
          wellbeing: ['Needs Support', 'Fair', 'Good', 'Excellent'][ci.score],
          wellbeing_score: ci.score,
        })),
      ),
      map((checkIns) => {
        return DataExportActions.requestSaveFile({
          data: Papa.unparse(checkIns),
          mimeType: 'text/csv',
          filename: 'customer_wellbeing_checks.csv',
        });
      })
    )
  );

  exportQuestionAnswers = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestExportQuestionAnswers),
      withLatestFrom(
        this.store.select(getEntities(selectors.QUESTIONS)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getEntities(selectors.ORGANISATIONS))
      ),
      mergeMap(([action, questions, organisationId, organisations]) =>
        this.questionsService
          .getAnswers({ id: action.questionId, organisationId: organisationId ?? undefined })
          .pipe(
            map((data) => {
              const question: QuestionDto = questions[action.questionId];
              let options: string[];
              if (question.type === YESNO) {
                options = ['No', 'Yes'];
              } else if (question.type === POLL) {
                options = [...question.configuration.options];
                if (question.configuration.allowOther) {
                  options.push('Other');
                }
              }

              const flattened = data.map((answer) => {
                let selection = {};
                if (question.type === YESNO || question.type === POLL) {
                  for (let i = 0; i < options.length; i++) {
                    selection[options[i]] = answer.selection.includes(i) ? 1 : 0;
                  }
                } else if (question.type === RATING) {
                  selection = { Rating: answer.selection[0] };
                }
                return {
                  Timestamp: answer.created,
                  ...selection,
                  Comment: answer.body,
                  Site: answer.organisationId
                    ? organisations[answer.organisationId]?.name
                    : undefined,
                };
              });

              const csv = Papa.unparse(flattened);

              return DataExportActions.requestSaveFile({
                filename: `question-responses-${action.questionId}.csv`,
                mimeType: 'text/csv',
                data: csv,
              });
            }),
            catchError((error) => {
              return of(DataExportActions.responseFailDataExport({ error }));
            })
          )
      )
    )
  );

  exportFile = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.requestSaveFile),
      map((action) => {
        const blob: Blob = new Blob([action.data]);
        FileSaver.saveAs(blob, action.filename);
        return DataExportActions.successSaveFile();
      })
    )
  );

  exportComments = createEffect(() =>
    this.actions$.pipe(
      ofType(DataExportActions.exportRecentComments),
      withLatestFrom(
        this.store.select(statistics.getRecentMemoComments),
        this.store.select(getEntities(selectors.ORGANISATIONS)),
        this.store.select(getEntities(selectors.CUSTOMERS)),
      ),
      map(([action, comments, organisations]) => {
        return comments.map((row) => ({
          Date: formatISO9075(parseISO(row.created)),
          Comment: row.body,
          'Site name': (organisations as { [id: string]: OrganisationDto })?.[row.organisationId]
            ?.name,
          'Linked customer first name': row.relevantCustomer?.firstName,
          'Linked customer last name': row.relevantCustomer?.lastName,
          'Author first name': row.member?.firstName,
          'Author last name': row.member?.lastName,
          'Link to post': row.modelId
            ? `${CaEnvironment.APP_BASE_URL.replace('#', '')}${row.tenantId}/organisations/${row.organisationId}/messages/${row.modelId}`
            : null,
        }));
      }),
      map((data) => Papa.unparse(data)),
      map((data) =>
        DataExportActions.requestSaveFile({
          filename: 'comments.csv',
          mimeType: 'text/csv',
          data,
        }),
      ),
    ),
  );

  private async buildFeedbackCsvForOrganisations(
    action: { organisationIds: string[] },
    organisations: OrganisationDto[],
    filters: {
      startDate: Date;
      endDate: Date;
    }
  ) {
    // we need to get all the members for each of the organisations
    let combinedMembers = {};
    let orgStats = {};
    for (const organisationId of action.organisationIds) {
      const orgMembers = await this.membersService.listMembers({ organisationId }).toPromise();
      const entities = orgMembers.reduce((acc, entity) => ({ ...acc, [entity.id]: entity }), {});
      combinedMembers = { ...combinedMembers, ...entities };
    }

    // now we need to get the feedback for each organisation
    for (const orgId of action.organisationIds) {
      const stats = await this.feedbackService
        .listFeedback({
          organisationId: orgId,
          endDate: filters?.endDate?.toISOString(),
          startDate: filters?.startDate?.toISOString(),
        })
        .toPromise();

      orgStats = { ...orgStats, [orgId]: stats };
    }

    // merge to csv
    let combinedStats = [];
    for (const orgId of action.organisationIds) {
      combinedStats = [
        ...combinedStats,
        ...orgStats[orgId].map((row) => ({
          organisation: organisations[orgId].name,
          created: row.created,
          firstName: combinedMembers[row.memberId] ? combinedMembers[row.memberId].firstName : '',
          lastName: combinedMembers[row.memberId] ? combinedMembers[row.memberId].lastName : '',
          email: combinedMembers[row.memberId] ? combinedMembers[row.memberId].email : '',
          message: row.message,
          rating: row.rating,
        })),
      ];
    }
    return Papa.unparse(combinedStats);
  }

  private async buildCommentsCsvForOrganisations(
    action: { organisationIds: string[] },
    organisations: { [id: string]: OrganisationDto },
    filters: {
      startDate: Date;
      endDate: Date;
    }
  ): Promise<string> {
    const orgIdsByTenant = new Map<string, string[]>();
    for (const orgId of action.organisationIds) {
      const org = organisations[orgId];
      const tenantId = org?.tenantId;
      if (tenantId) {
        const ids = orgIdsByTenant.get(tenantId) ?? [];
        ids.push(orgId);
        orgIdsByTenant.set(tenantId, ids);
      }
    }

    let allComments: any[] = [];
    for (const [tenantId, orgIds] of orgIdsByTenant) {
      const comments = await this.commentsService
        .listCommentsV2({
          listCommentsQueryDto: {
            tenantId,
            organisationIds: orgIds,
            startDate: filters?.startDate?.toISOString(),
            endDate: filters?.endDate?.toISOString(),
            skip: 0,
          },
        })
        .toPromise();
      allComments = [...allComments, ...(comments ?? [])];
    }

    const combinedStats = allComments.map((row) => ({
      Date: formatISO9075(parseISO(row.created)),
      Comment: row.body,
      'Site name': organisations[row.organisationId]?.name,
      'Linked customer first name': row.relevantCustomer?.firstName,
      'Linked customer last name': row.relevantCustomer?.lastName,
      'Author first name': row.member?.firstName,
      'Author last name': row.member?.lastName,
      'Link to post': row.modelId
        ? `${CaEnvironment.APP_BASE_URL.replace('#', '')}${row.tenantId}/organisations/${row.organisationId}/messages/${row.modelId}`
        : null,
    }));

    return Papa.unparse(combinedStats);
  }
}
